/** Worker access: where it is, the demo token, and a stable per-browser device id. */
import { featureCollector, warmRoute, type FeatureFixture, type TrackPoint, type WarmProgress, type WarmedLandmark } from '@roadtrip/core';

const KEY = 'routelab.settings';
const DEFAULT_BASE = import.meta.env.DEV ? 'http://127.0.0.1:8787' : 'https://roadtrip-worker.nchristen713.workers.dev';

export interface Settings {
  baseUrl: string;
  token: string;
  deviceId: string;
  voice: string;
  rate: number;
  chime: boolean;
}

function read(): Partial<Settings> {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<Settings>;
  } catch {
    return {};
  }
}

export function loadSettings(): Settings {
  const s = read();
  const deviceId = s.deviceId ?? `web-${crypto.randomUUID().slice(0, 8)}`;
  const out: Settings = {
    baseUrl: s.baseUrl ?? DEFAULT_BASE,
    token: s.token ?? (import.meta.env.DEV ? 'local-dev' : ''),
    deviceId,
    voice: s.voice ?? '',
    rate: s.rate ?? 1,
    chime: s.chime ?? true,
  };
  if (!s.deviceId) saveSettings(out);
  return out;
}

export function saveSettings(s: Settings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* private mode; settings live for the page only */
  }
}

export async function checkHealth(baseUrl: string): Promise<{ ok: boolean; detail: string }> {
  try {
    const res = await fetch(`${baseUrl.replace(/\/$/, '')}/v1/health`);
    if (!res.ok) return { ok: false, detail: `health ${res.status}` };
    const j = (await res.json()) as { ok?: boolean; prompt_version?: string; api_key_len?: number };
    if (!j.api_key_len) return { ok: true, detail: `${j.prompt_version}, no API key: cached landmarks only` };
    return { ok: true, detail: `prompt ${j.prompt_version}` };
  } catch (e) {
    return { ok: false, detail: `unreachable (${(e as Error).message})` };
  }
}

/** Landmarks along the route, and the rivers, forests and wilderness the corridor calls sent. */
export async function warm(
  track: TrackPoint[],
  s: Settings,
  onProgress: (p: WarmProgress) => void,
  signal: AbortSignal,
): Promise<{ landmarks: WarmedLandmark[]; features: FeatureFixture | undefined }> {
  const collect = featureCollector();
  const landmarks = await warmRoute(track, {
    baseUrl: s.baseUrl,
    deviceId: s.deviceId,
    demoToken: s.token || undefined,
    onProgress,
    onCorridor: (pt, r) => collect.add(pt, r),
    signal,
  });
  return { landmarks, features: collect.fixture() };
}
